import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Puzzle, Server, GraduationCap, Check, ArrowRight } from 'lucide-react';

const featureLinks = [
  { icon: Puzzle, href: '/services#integration' },
  { icon: Server, href: '/zimbra-admin-panel' },
  { icon: GraduationCap, href: '/training' },
];

interface Feature {
  title: string;
  description: string;
}

const CompanyDescription = () => {
  const { t } = useTranslation('company');
  const { t: te } = useTranslation('editor');
  const rawFeatures = t('features', { returnObjects: true });
  const features = Array.isArray(rawFeatures) ? (rawFeatures as Feature[]) : [];
  const rawPoints = t('whyUs.points', { returnObjects: true });
  const points = Array.isArray(rawPoints) ? (rawPoints as string[]) : [];

  return (
    <section className="relative py-24 bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark opacity-50" />
      <div className="pointer-events-none absolute -top-32 right-0 w-[600px] h-[400px] rounded-full bg-cyan-500/10 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">{t('title')}</h2>
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">{t('subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {features.map((feature, i) => {
            const link = featureLinks[i] ?? featureLinks[0];
            const Icon = link.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link
                  to={link.href}
                  className="group flex flex-col h-full glass-card rounded-3xl p-7 hover:border-cyan-300/40 hover:-translate-y-1 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center mb-5 transition-transform group-hover:scale-110 group-hover:-rotate-3">
                    <Icon className="h-5 w-5 text-slate-950" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed flex-1">{feature.description}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 group-hover:text-white transition-colors">
                    {te('home.learnMore')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 rounded-3xl p-px bg-gradient-to-br from-cyan-300/40 via-white/10 to-emerald-300/30"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center rounded-3xl bg-slate-900/90 p-8 lg:p-10">
            <div>
              <h3 className="font-display text-2xl lg:text-3xl font-bold text-white mb-6">{t('whyUs.title')}</h3>
              <ul className="space-y-4 mb-8">
                {points.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center mt-0.5">
                      <Check className="h-3.5 w-3.5 text-emerald-300" />
                    </span>
                    <span className="text-slate-300">{point}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/company"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 hover:shadow-[0_0_32px_-6px_rgba(34,211,238,0.9)] transition-shadow"
              >
                {te('home.learnMore')}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80"
                alt=""
                loading="lazy"
                className="w-full h-72 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/70 via-transparent to-cyan-500/20" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CompanyDescription;
